import type { FindAllListingsDTO, Listing, ListingType } from 'app/types/listing';
import { useSelectedGroupsAndAddresses } from './listing';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { http } from 'app/lib/axios';
import { getBffQueryData, useQueryLPC } from 'app/lib/query';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import type {
  OneToManySimilaritiesQueryDTO,
  ManyToManySimilaritiesQueryDTO,
  GetRecommendationsWithSimilarityQueryDTO,
  RecommendedListing,
  InteractionType,
  FeatureType,
} from 'app/types/recommender';
import { bffUserProfileLoadedAtom } from 'app/atoms';
import { useAtomValue } from 'jotai';
import { mergeListingDetailCache } from './listing-cache';
import { recommendationKeys } from './keys';

export { recommendationKeys } from './keys';

export function useOneToManySimilarListings(
  recommendationType: ListingType,
  similarityType: InteractionType | FeatureType,
  listingId: Listing['id'],
  opts: FunctionalUseQueryOptions<RecommendedListing[]> = {},
  useBff = false,
  top_n?: number,
) {
  const featureEnabled = isFeatureEnabled(FeatureFlag.similarListings);

  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const findAllListingsDTO: FindAllListingsDTO = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    type: recommendationType,
  };

  const queryParams: OneToManySimilaritiesQueryDTO = {
    listingId,
    similarityType,
    top_n,
    ...findAllListingsDTO,
  };

  const queryKey = recommendationKeys.list('similars', { ...queryParams });

  return useRecommendationQuery(
    queryKey,
    '/recommender/listing/similarities',
    queryParams,
    useBff,
    featureEnabled,
    loaded,
    opts,
  );
}

export function useMatchNeedsWithOffers(
  recommendationType: ListingType,
  similarityType: InteractionType | FeatureType,
  opts: FunctionalUseQueryOptions<RecommendedListing[]> = {},
  useBff = false,
) {
  const featureEnabled = isFeatureEnabled(FeatureFlag.similarListings);

  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const findAllListingsDTO: FindAllListingsDTO = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    type: recommendationType,
  };

  const queryParams: ManyToManySimilaritiesQueryDTO = {
    similarityType,
    ...findAllListingsDTO,
  };

  const queryKey = recommendationKeys.list('need-match', queryParams);

  return useRecommendationQuery(
    queryKey,
    '/recommender/match-needs-with-offers',
    queryParams,
    useBff,
    featureEnabled,
    loaded,
    opts,
  );
}

export function usePredictions(
  recommendationType: ListingType,
  opts: FunctionalUseQueryOptions<RecommendedListing[]> = {},
  useBff = false,
) {
  const featureEnabled = isFeatureEnabled(FeatureFlag.predictions);

  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const findAllListingsDTO: FindAllListingsDTO = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    type: recommendationType,
  };

  const queryKey = recommendationKeys.list('predicted', { ...findAllListingsDTO });

  return useRecommendationQuery(
    queryKey,
    '/recommender/user/predictions',
    findAllListingsDTO,
    useBff,
    featureEnabled,
    loaded,
    opts,
  );
}

export function useMixedRecommendations(
  recommendationType: ListingType,
  similarityType: InteractionType | FeatureType,
  opts: FunctionalUseQueryOptions<RecommendedListing[]> = {},
  useBff = false,
) {
  const featureEnabled = isFeatureEnabled(FeatureFlag.predictions);

  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const findAllListingsDTO: FindAllListingsDTO = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    type: recommendationType,
  };

  const queryParams: GetRecommendationsWithSimilarityQueryDTO = {
    similarityType,
    ...findAllListingsDTO,
  };

  const queryKey = recommendationKeys.list('mixed-recommendations', { ...queryParams });

  return useRecommendationQuery(
    queryKey,
    '/recommender/user/mixed-recommendations',
    queryParams,
    useBff,
    featureEnabled,
    loaded,
    opts,
  );
}

export function usePastInteractions(
  recommendationType: ListingType,
  opts: FunctionalUseQueryOptions<RecommendedListing[]> = {},
  useBff = false,
) {
  const featureEnabled = isFeatureEnabled(FeatureFlag.predictions);

  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const findAllListingsDTO: FindAllListingsDTO = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    type: recommendationType,
  };

  const queryKey = recommendationKeys.list('past-interactions', { ...findAllListingsDTO });

  return useRecommendationQuery(
    queryKey,
    '/recommender/user/interactions',
    findAllListingsDTO,
    useBff,
    featureEnabled,
    loaded,
    opts,
  );
}

export function useTestRecommendations(
  recommendationType: ListingType,
  opts: FunctionalUseQueryOptions<RecommendedListing[]> = {},
  useBff = false,
) {
  const featureEnabled = isFeatureEnabled(FeatureFlag.predictions);

  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const findAllListingsDTO: FindAllListingsDTO = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    type: recommendationType,
  };

  const queryKey = recommendationKeys.list('test-recommendations', { ...findAllListingsDTO });

  return useRecommendationQuery(
    queryKey,
    '/recommender/user/test',
    findAllListingsDTO,
    useBff,
    featureEnabled,
    loaded,
    opts,
  );
}

export function useUserItemPredictions(
  recommendationType: ListingType,
  similarityType: InteractionType | FeatureType,
  opts: FunctionalUseQueryOptions<RecommendedListing[]> = {},
  useBff = false,
) {
  const featureEnabled = isFeatureEnabled(FeatureFlag.predictions);

  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const findAllListingsDTO: FindAllListingsDTO = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    type: recommendationType,
  };

  const queryParams: GetRecommendationsWithSimilarityQueryDTO = {
    similarityType: similarityType,
    ...findAllListingsDTO,
  };

  const queryKey = recommendationKeys.list('user-item-predictions', { ...queryParams });

  return useRecommendationQuery(
    queryKey,
    '/recommender/user/useritem',
    queryParams,
    useBff,
    featureEnabled,
    loaded,
    opts,
  );
}

function useRecommendationQuery(
  queryKey: QueryKey,
  endpoint: string,
  queryParams: object | undefined,
  useBff: boolean,
  featureEnabled: boolean,
  loaded: boolean,
  opts: FunctionalUseQueryOptions<RecommendedListing[]> = {},
) {
  const client = useQueryClient();
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);

  const queryFn = async () =>
    (useBff && bffUserProfileLoaded ? getBffQueryData<RecommendedListing[]>(client, queryKey) : undefined) ??
    (await http.get<RecommendedListing[]>(endpoint, queryParams ? { params: queryParams } : undefined)).data;

  return useQueryLPC<RecommendedListing[]>({
    queryKey,
    queryFn: async () =>
      queryFn().then((data) => {
        data.forEach((listing) => mergeListingDetailCache(client, listing));
        return data;
      }),
    ...opts,
    enabled: featureEnabled && (opts.enabled ?? true) && loaded && (!useBff || bffUserProfileLoaded),
  });
}
